import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getLastCheckpointDoc } from 'rxdb';
import { Subscription } from 'rxjs';
import * as ApiModels from '@/typings/questionnaires-api-models';
import { getNoodleDb } from '../rxdb.noodle';
import { getQuestionnaireTemplateReplicationIdentifier, getReplicationDataByIdentifier, ReplicationIdentifier, startOrResyncReplication } from '../rxdb.replication';

type Config = {
  realtime?: boolean;
  enabled?: boolean;
  refetchOnMount?: boolean;
};

type ReturnType = {
  hadUpdates: boolean | null;
  isFirstPull: boolean | null;
  item: ApiModels.QuestionnaireTemplateNoFields | null;
  refresh: () => void;
};

const getIdentifier = (): ReplicationIdentifier => getQuestionnaireTemplateReplicationIdentifier();

function useQuestionnaireTemplate(
  questionnaireTemplateId?: string,
  { realtime = true, enabled = true, refetchOnMount = true }: Config = {},
): ReturnType {
  const subscription = useRef<Subscription | null>(null);
  const hasBeenRefetchedOnMount = useRef(false);

  const [questionnaireTemplate, setQuestionnaireTemplate] = useState<ApiModels.QuestionnaireTemplateNoFields | null>(null);

  const [isFirstPull, setIsFirstPull] = useState<boolean | null>(null);
  const [hadUpdates, setHadUpdates] = useState<boolean | null>(null);

  const getQuestionnaireTemplate = useCallback(async () => {
    try {
      if (!questionnaireTemplateId) return;

      const db = await getNoodleDb();

      subscription.current = db.questionnaireTemplate.findOne({ selector: { id: questionnaireTemplateId } }).$.subscribe(rxQuestionnaireTemplate => {
        if (!rxQuestionnaireTemplate) return;

        const item = rxQuestionnaireTemplate.toMutableJSON() as ApiModels.QuestionnaireTemplateNoFields;
        setQuestionnaireTemplate(item);

        // If not realtime, unsubscribe after first value is set
        if (!realtime) {
          subscription.current?.unsubscribe();
        }
      });
    } catch (error) {
      console.error('RXDB useQuestionnaireTemplate error - getQuestionnaireTemplate()', error);
    }
  }, [questionnaireTemplateId, realtime]);

  const handleRefetch = useCallback(async () => {
    const identifier = getIdentifier();
    startOrResyncReplication(identifier);

    const replicationData = await getReplicationDataByIdentifier(identifier, { waitUntilReplicationIsAdded: true });
    if (!replicationData || !replicationData.state) return;

    const pullHappenedSubscription = replicationData.onPullHappened.subscribe(data => {
      setHadUpdates(data.documents.length > 0);
      pullHappenedSubscription.unsubscribe();
    });
  }, []);

  const handleRefetchOnMount = useCallback(() => {
    if (!refetchOnMount || hasBeenRefetchedOnMount.current) return;

    hasBeenRefetchedOnMount.current = true;
    handleRefetch();
  }, [handleRefetch, refetchOnMount]);

  const checkIfFirstPull = useCallback(async () => {
    if (!questionnaireTemplateId) return;

    const identifier = getIdentifier();
    const replicationData = await getReplicationDataByIdentifier(identifier, { waitUntilReplicationIsAdded: true });
    if (!replicationData || !replicationData.state) return;

    await replicationData.state.startPromise;

    const lastCheckpoint = await getLastCheckpointDoc(replicationData.state.internalReplicationState!, 'down');
    setIsFirstPull(!lastCheckpoint);
  }, [questionnaireTemplateId]);

  useEffect(() => {
    if (enabled) {
      checkIfFirstPull();
      getQuestionnaireTemplate();
      handleRefetchOnMount();
    }

    return () => {
      subscription.current?.unsubscribe();
    };
  }, [checkIfFirstPull, getQuestionnaireTemplate, enabled, handleRefetchOnMount]);

  const memoizedReturn = useMemo(() => ({
    hadUpdates,
    isFirstPull,
    item: questionnaireTemplate,
    refresh: handleRefetch,
  }), [questionnaireTemplate, handleRefetch, hadUpdates, isFirstPull]);

  return memoizedReturn;
}

export default useQuestionnaireTemplate;
